import React, { useMemo } from 'react';
import { Button, Card, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { AdvertisementPosition, AdvertisementResponse, AdvertisementSlotResponse, AdvertisementType } from '../../types';
import {
  getAdvertisementPositionValue,
  normalizeAdvertisementPagePath,
  slotMatchesAdvertisementArea,
  storeAdvertisementIntent
} from '../../utils/advertisementSlots';
import { getAdvertisementImageSrc } from '../../utils/advertisementImage';
import OptimizedImage from './OptimizedImage';

const { Text } = Typography;

interface AdvertisementDisplayProps {
  pagePath: string;
  type: AdvertisementType;
  position?: 'top' | 'bottom' | 'inline' | 'sidebar';
  maxCount?: number;
  className?: string;
}

const AdvertisementDisplay: React.FC<AdvertisementDisplayProps> = ({
  pagePath,
  type,
  position = 'inline',
  maxCount = 1,
  className
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const normalizedPagePath = useMemo(() => normalizeAdvertisementPagePath(pagePath), [pagePath]);
  const positionValue = useMemo(() => getAdvertisementPositionValue(position), [position]);

  const { data: ads = [] } = useQuery({
    queryKey: ['advertisements', normalizedPagePath, type, positionValue, maxCount],
    queryFn: async () => {
      const response = await apiService.getActiveAdvertisements(type, normalizedPagePath, positionValue);
      if (!response.success) throw new Error(response.message);
      return (response.data ?? []).slice(0, maxCount);
    },
    staleTime: 1000 * 60,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { data: slots = [] } = useQuery({
    queryKey: ['advertisement-slots-public', normalizedPagePath, positionValue],
    queryFn: async () => {
      const response = await apiService.getPublicAdvertisementSlots(normalizedPagePath, positionValue);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60,
  });

  const matchingSlots = useMemo(
    () =>
      slots.filter((slot: AdvertisementSlotResponse) =>
        slotMatchesAdvertisementArea(slot, normalizedPagePath, type, positionValue)
      ),
    [slots, normalizedPagePath, type, positionValue]
  );

  const selectedSlotForCta = matchingSlots[0] ?? null;

  const handleCtaClick = () => {
    if (!selectedSlotForCta) return;
    storeAdvertisementIntent(selectedSlotForCta.id);
    navigate('/profile?tab=advertisements');
  };

  const renderEmptyAdSlot = () => {
    if (user?.role !== 'Mentor' || !selectedSlotForCta) {
      return null;
    }

    return (
      <div
        style={{
          width: '100%',
          padding: position === 'top' ? '36px 24px' : '28px 20px',
          textAlign: 'center',
          border: '2px dashed #d9d9d9',
          borderRadius: '12px',
          marginBottom: position === 'top' ? '24px' : 0,
          background: '#fafafa'
        }}
      >
        <Text type="secondary" style={{ fontSize: '16px', display: 'block' }}>
          Buraya reklam verebilirsiniz
        </Text>
        <Button type="link" onClick={handleCtaClick} style={{ marginTop: '8px' }}>
          Reklam alanini sec
        </Button>
      </div>
    );
  };

  const handleAdClick = async (ad: AdvertisementResponse) => {
    if (ad.linkUrl) {
      window.open(ad.linkUrl, '_blank', 'noopener,noreferrer');
    }
    await apiService.recordAdvertisementClick(ad.id);
  };

  const renderBannerAd = (ad: AdvertisementResponse) => (
    <div
      key={ad.id}
      style={{
        width: '100%',
        marginBottom: '24px',
        cursor: ad.linkUrl ? 'pointer' : 'default',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-sm)',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      onClick={() => handleAdClick(ad)}
      onMouseEnter={(e) => {
        if (ad.linkUrl) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
        }
      }}
      onMouseLeave={(e) => {
        if (ad.linkUrl) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
        }
      }}
    >
      <OptimizedImage
        src={getAdvertisementImageSrc(ad.imageBase64, ad.imageContentType)}
        alt={ad.title || 'Banner Reklam'}
        width="100%"
        height="auto"
        loading="lazy"
        placeholder="blur"
        aria-label={ad.title || 'Banner Reklam'}
      />
    </div>
  );

  const renderCardAd = (ad: AdvertisementResponse) => (
    <Card
      key={ad.id}
      hoverable={!!ad.linkUrl}
      style={{
        height: '100%',
        cursor: ad.linkUrl ? 'pointer' : 'default',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid var(--border-light)',
        boxShadow: 'var(--shadow-sm)',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        if (ad.linkUrl) {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
        }
      }}
      onMouseLeave={(e) => {
        if (ad.linkUrl) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
        }
      }}
      onClick={() => handleAdClick(ad)}
      cover={
        <OptimizedImage
          src={getAdvertisementImageSrc(ad.imageBase64, ad.imageContentType)}
          alt={ad.title || 'Card Reklam'}
          width="100%"
          height="200px"
          objectFit="cover"
          loading="lazy"
          placeholder="blur"
          aria-label={ad.title || 'Card Reklam'}
        />
      }
    >
      {ad.title && (
        <Card.Meta
          title={ad.title}
          description={ad.description}
        />
      )}
    </Card>
  );

  if (ads.length === 0) {
    return renderEmptyAdSlot();
  }

  return (
    <div className={className} style={{ width: '100%' }}>
      {ads.map((ad) => 
        type === AdvertisementType.Banner ? renderBannerAd(ad) : renderCardAd(ad)
      )}
    </div>
  );
};

export default AdvertisementDisplay;
